import { useCallback, useEffect, useRef, useState } from "react";

import { aiFillSectionUri } from "@smart/bridge-types-basic";

import {
  DEFAULT_FIRST_WORD_GRACE_MS,
  DEFAULT_MAX_UTTERANCE_MS,
  DEFAULT_REPLY_TIMEOUT_MS,
  DEFAULT_SILENCE_THRESHOLD_MS,
  getInitialGreeting,
  getLocaleLanguage,
  getSpeechRecognition,
  validateFieldValue,
  VoiceConversationOptions,
  VoiceConversationWebSocketApi,
  VoiceSpeechRecognition,
} from "./helpers";

export const useVoiceConversation = (options: VoiceConversationOptions) => {
  const selectedLanguage = options.language || "en";

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const getAudio = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    return audioRef.current;
  };

  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startRecognitionRef = useRef<(() => void) | null>(null);
  const expectingReplyRef = useRef(false);
  const replyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingMoveAfterUploadRef = useRef(false);
  const uploadPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const fileSelectListenerRef = useRef<{
    input: HTMLInputElement;
    handler: () => void;
  } | null>(null);

  const stateRef = useRef<{
    recognition: VoiceSpeechRecognition | null;
    websocket: ReturnType<
      VoiceConversationWebSocketApi["voiceConversationWebSocket"]
    > | null;
    shouldKeepListening: boolean;
    conversationHistory: Array<{
      role: "user" | "assistant";
      content:
        | string
        | (
            | {
                type: "tool_use";
                id: string;
                name: string;
                input: any;
              }
            | {
                type: "tool_result";
                tool_use_id: string;
                content: string;
              }
          )[];
    }>;
    lastProcessedTranscript: string;
    isProcessing: boolean;
    isInitialized: boolean;
    options: VoiceConversationOptions;
  }>({
    recognition: null,
    websocket: null,
    shouldKeepListening: false,
    conversationHistory: [],
    lastProcessedTranscript: "",
    isProcessing: false,
    isInitialized: false,
    options,
  });

  const websocket = options.voiceWebSocket.voiceConversationWebSocket({
    actionRouteKey: "voiceMessage",
  });
  stateRef.current.websocket = websocket;

  useEffect(() => {
    stateRef.current.options = options;
  }, [options]);

  const clearReplyTimeout = () => {
    if (replyTimeoutRef.current) {
      clearTimeout(replyTimeoutRef.current);
      replyTimeoutRef.current = null;
    }
    expectingReplyRef.current = false;
  };

  const setReplyTimeout = () => {
    clearReplyTimeout();
    expectingReplyRef.current = true;
    const ms =
      stateRef.current.options.replyTimeoutMs ?? DEFAULT_REPLY_TIMEOUT_MS;
    replyTimeoutRef.current = setTimeout(() => {
      replyTimeoutRef.current = null;
      if (!stateRef.current.isProcessing) return;

      console.warn("Voice AI reply timed out; resetting processing state.");
      stateRef.current.isProcessing = false;
      expectingReplyRef.current = false;
      setError("The assistant didn't respond. Please try again.");

      if (stateRef.current.shouldKeepListening) {
        startRecognitionRef.current?.();
      }
    }, ms);
  };

  const sendUserMessage = async (
    content: string,
    currentSectionUriOverride?: string,
  ) => {
    const opts = stateRef.current.options;
    const { submissionUri } = opts;
    if (!submissionUri) return;

    const entry = {
      role: "user" as const,
      content,
    };
    stateRef.current.conversationHistory.push(entry);
    try {
      await stateRef.current.websocket?.send({
        submissionUri,
        preview: opts.preview,
        currentSectionUri:
          currentSectionUriOverride ||
          opts.selectedSection?.uri ||
          opts.visibleSections?.[0]?.uri ||
          "",
        conversationHistory: stateRef.current.conversationHistory,
        language: opts.language || "en",
      });
      setReplyTimeout();
    } catch (err) {
      console.error("Error sending user message", err);
      const idx = stateRef.current.conversationHistory.lastIndexOf(entry);
      if (idx >= 0) {
        stateRef.current.conversationHistory.splice(idx, 1);
      }
      stateRef.current.isProcessing = false;
      stateRef.current.lastProcessedTranscript = "";
      clearReplyTimeout();
      setError("Failed to send message. Please try again.");
    }
  };

  const stopUploadPolling = () => {
    if (uploadPollIntervalRef.current) {
      clearInterval(uploadPollIntervalRef.current);
      uploadPollIntervalRef.current = null;
    }
  };

  const clearFileSelectListener = () => {
    if (fileSelectListenerRef.current) {
      fileSelectListenerRef.current.input.removeEventListener(
        "change",
        fileSelectListenerRef.current.handler,
      );
      fileSelectListenerRef.current = null;
    }
  };

  const startUploadPolling = () => {
    if (uploadPollIntervalRef.current) return;
    uploadPollIntervalRef.current = setInterval(() => {
      if (!pendingMoveAfterUploadRef.current) {
        stopUploadPolling();
        return;
      }
      const stillUploading =
        stateRef.current.options.sectionRef?.current?.hasUploadingFiles?.();
      if (!stillUploading && !stateRef.current.isProcessing) {
        pendingMoveAfterUploadRef.current = false;
        stopUploadPolling();
        stateRef.current.isProcessing = true;
        sendUserMessage("[FILE_UPLOAD_COMPLETE]").catch(console.error);
      }
    }, 2000);
  };

  type ToolUse =
    | { type: "fillField"; fieldUri: string; value?: any; id: string }
    | { type: "moveToNextSection"; id: string }
    | { type: "submit"; id: string }
    | { type: "scrollToFileField"; fieldUri: string; id: string };

  type ToolUseResult = {
    id: string;
    success: boolean;
    reason?: string;
  };

  const processActions = useCallback((actions: ToolUse[]): ToolUseResult[] => {
    const currentOptions = stateRef.current.options;

    if (!currentOptions?.submitResponses || !currentOptions?.fields) {
      return actions.map((a) => ({
        id: a.id,
        success: false,
        reason: "configuration error",
      }));
    }

    const hasSubmitAction = actions.some((a) => a.type === "submit");

    const mapped = actions.map(
      (
        action,
      ): {
        result: ToolUseResult;
        update?: {
          fieldUri: string;
          processedValue: any;
          sectionUri: string;
        };
      } => {
        if (action.type === "moveToNextSection") {
          if (currentOptions.sectionRef?.current?.hasUploadingFiles?.()) {
            pendingMoveAfterUploadRef.current = true;
            startUploadPolling();
            return {
              result: {
                id: action.id,
                success: false,
                reason: "files are still uploading, please wait",
              },
            };
          }
          return { result: { id: action.id, success: true } };
        }

        if (action.type === "submit") {
          return { result: { id: action.id, success: true } };
        }

        if (action.type === "scrollToFileField") {
          const input = document.querySelector<HTMLInputElement>(
            `[data-voice-file-field="${action.fieldUri}"]`,
          );
          if (input) {
            input.scrollIntoView({ behavior: "smooth", block: "center" });
            clearFileSelectListener();
            const handler = () => {
              fileSelectListenerRef.current = null;
              if (!stateRef.current.isProcessing) {
                stateRef.current.isProcessing = true;
                sendUserMessage("[FILE_SELECTED]").catch(console.error);
              }
            };
            input.addEventListener("change", handler, { once: true });
            fileSelectListenerRef.current = { input, handler };
            return { result: { id: action.id, success: true } };
          }
          return {
            result: {
              id: action.id,
              success: false,
              reason: "file picker not available",
            },
          };
        }

        const field = currentOptions.fields?.find(
          (f) => f.uri === action.fieldUri,
        );
        if (!field) {
          return {
            result: {
              id: action.id,
              success: false,
              reason: "field not found",
            },
          };
        }
        let processedValue: any = action.value;
        const validated = validateFieldValue(field, action.value);
        if (!validated.success) {
          return {
            result: {
              id: action.id,
              success: false,
              reason: validated.reason,
            },
          };
        }
        processedValue = validated.value;
        const isGroup = !!field.groupUri;
        if (isGroup && !Array.isArray(processedValue)) {
          processedValue = [processedValue];
        }
        return {
          result: { id: action.id, success: true },
          update: {
            fieldUri: action.fieldUri,
            processedValue,
            sectionUri: field.sectionUri,
          },
        };
      },
    );

    const results = mapped.map((m) => m.result);
    const responses: Record<string, any> = {};
    let lastUpdatedFieldUri: string | undefined;
    let lastUpdatedSectionUri: string | undefined;

    mapped.forEach(({ update }) => {
      if (!update) return;
      responses[update.fieldUri] = update.processedValue;
      lastUpdatedFieldUri = update.fieldUri;
      lastUpdatedSectionUri = update.sectionUri;
      currentOptions?.sectionRef?.current?.setFieldValue?.(
        update.fieldUri,
        update.processedValue,
      );
    });

    if (Object.keys(responses).length > 0) {
      currentOptions
        .submitResponses({
          responses,
          lastUpdatedFieldUri,
          lastUpdatedSectionUri,
        })
        .catch(console.error);
    }

    const hasMoveAction = results.some(
      (r) =>
        r.success &&
        actions.find((a) => a.id === r.id)?.type === "moveToNextSection",
    );

    if (
      hasMoveAction &&
      currentOptions?.visibleSections &&
      currentOptions?.selectedSection
    ) {
      const selectedIndex = currentOptions.visibleSections.findIndex(
        (s) => s.uri === currentOptions.selectedSection?.uri,
      );
      if (currentOptions.selectedSection.uri === aiFillSectionUri) {
        const firstSection = currentOptions.visibleSections[0];
        if (firstSection && currentOptions?.onSelectSection) {
          currentOptions.onSelectSection(firstSection);
        }
      } else {
        const nextSection = currentOptions.visibleSections[selectedIndex + 1];
        if (nextSection && currentOptions?.onSelectSection) {
          currentOptions.onSelectSection(nextSection);
        } else if (!nextSection && currentOptions?.onSummary) {
          currentOptions.onSummary();
        }
      }
    }

    if (hasSubmitAction && currentOptions?.submitResponses) {
      currentOptions
        .submitResponses({
          responses: {},
          status: "completed",
        })
        .catch(console.error);
    }

    return results;
  }, []);

  const playResponse = useCallback(
    (preSignedUrl: string, onEnd?: () => void) => {
      const audio = getAudio();
      let endedFired = false;
      const fireOnEnd = () => {
        if (endedFired) return;
        endedFired = true;
        setIsSpeaking(false);
        onEnd?.();
      };

      audio.src = preSignedUrl;
      audio.onended = fireOnEnd;
      audio.onerror = fireOnEnd;
      setIsSpeaking(true);

      const playResult = audio.play() as Promise<void> | undefined;
      // Preventative catch to ensure play() always resolves
      if (playResult !== undefined && playResult !== null) {
        playResult.catch((err) => {
          console.error("audio.play() rejected:", err);
          fireOnEnd();
        });
      }
    },
    [],
  );

  // Single long-lived SpeechRecognition instance with continuous + interim
  // results. Turn-taking is done via three timers:
  //   - silenceTimer:       fires SILENCE_MS after the most recent interim
  //                         update; commits the buffered transcript.
  //   - maxUtteranceTimer:  fires MAX_MS after speech first started in the
  //                         current utterance; force-commits to bound runaway
  //                         sessions.
  //   - firstWordTimer:     fires FIRST_WORD_MS after recognition starts; if
  //                         no interim has arrived we let the engine keep
  //                         running (no spurious commit).
  //
  //
  const recognitionTimersRef = useRef<{
    silence: ReturnType<typeof setTimeout> | null;
    max: ReturnType<typeof setTimeout> | null;
    firstWord: ReturnType<typeof setTimeout> | null;
    restart: ReturnType<typeof setTimeout> | null;
  }>({ silence: null, max: null, firstWord: null, restart: null });

  // Cursor into recognition.results: every result with index < this has
  // already been committed and sent.
  const resultCommitCursorRef = useRef(0);
  // Most-recent buffered transcript (final + interim) for the current
  // utterance, joined and trimmed.
  const utteranceBufferRef = useRef("");
  const latestResultsLengthRef = useRef(0);
  const restartAttemptRef = useRef(0);
  const safeStartEngineRef = useRef<() => void>(() => {});
  const prevAutoFillStatusRef = useRef(options.autoFillStatus);

  const clearRecognitionTimers = () => {
    const t = recognitionTimersRef.current;
    if (t.silence) {
      clearTimeout(t.silence);
      t.silence = null;
    }
    if (t.max) {
      clearTimeout(t.max);
      t.max = null;
    }
    if (t.firstWord) {
      clearTimeout(t.firstWord);
      t.firstWord = null;
    }
    if (t.restart) {
      clearTimeout(t.restart);
      t.restart = null;
    }
  };

  useEffect(
    () => () => {
      stateRef.current.shouldKeepListening = false;
      clearRecognitionTimers();
      clearReplyTimeout();
      stopUploadPolling();
      pendingMoveAfterUploadRef.current = false;
      clearFileSelectListener();
      if (stateRef.current.recognition) {
        stateRef.current.recognition.abort();
        stateRef.current.recognition = null;
      }
      stateRef.current.websocket?.disconnect();
      if (audioRef.current) {
        audioRef.current.pause();
      }
    },
    [],
  );

  useEffect(() => {
    const prevStatus = prevAutoFillStatusRef.current;
    prevAutoFillStatusRef.current = options.autoFillStatus;

    if (
      prevStatus !== "completed" &&
      options.autoFillStatus === "completed" &&
      stateRef.current.shouldKeepListening &&
      !stateRef.current.isProcessing
    ) {
      const firstSectionUri =
        stateRef.current.options.visibleSections?.[0]?.uri;
      stateRef.current.isProcessing = true;
      sendUserMessage("[AUTOFILL_COMPLETE]", firstSectionUri).catch(
        console.error,
      );
    }
  }, [options.autoFillStatus]);

  const commitUtterance = () => {
    const t = recognitionTimersRef.current;
    if (t.silence) {
      clearTimeout(t.silence);
      t.silence = null;
    }
    if (t.max) {
      clearTimeout(t.max);
      t.max = null;
    }
    if (t.firstWord) {
      clearTimeout(t.firstWord);
      t.firstWord = null;
    }

    // Guard against stale-timer commits: if a silence/max timer scheduled
    // before isProcessing flipped to true is allowed to fire, we'd start a
    // second user turn while the first is still in flight.
    if (stateRef.current.isProcessing) {
      utteranceBufferRef.current = "";
      return;
    }

    const transcript = utteranceBufferRef.current.trim();
    utteranceBufferRef.current = "";

    if (!transcript) return;
    if (transcript === stateRef.current.lastProcessedTranscript) return;

    stateRef.current.lastProcessedTranscript = transcript;
    stateRef.current.isProcessing = true;
    sendUserMessage(transcript).catch((err) => {
      console.error("Unexpected error in sendUserMessage:", err);
    });
  };

  const scheduleRestart = () => {
    if (!stateRef.current.shouldKeepListening) return;
    const t = recognitionTimersRef.current;
    if (t.restart) return;

    const attempt = restartAttemptRef.current;
    restartAttemptRef.current = attempt + 1;
    // Backoff: 0, 250, 500, 1000, 2000 and 4000 (capped).
    const delay = Math.min(4000, attempt === 0 ? 0 : 250 * 2 ** (attempt - 1));
    t.restart = setTimeout(() => {
      t.restart = null;
      if (!stateRef.current.shouldKeepListening) return;
      safeStartEngineRef.current();
    }, delay);
  };

  const safeStartEngine = () => {
    const { recognition } = stateRef.current;
    if (!recognition) return;
    try {
      recognition.start();
      restartAttemptRef.current = 0;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (
        err &&
        typeof err === "object" &&
        "name" in err &&
        (err as { name?: string }).name === "InvalidStateError"
      ) {
        return;
      }
      console.error("Failed to start speech recognition:", message);
      scheduleRestart();
    }
  };
  safeStartEngineRef.current = safeStartEngine;

  const buildRecognitionInstance = (): VoiceSpeechRecognition | null => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) {
      setError("Speech recognition is not supported in this browser.");
      return null;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = getLocaleLanguage(
      stateRef.current.options.language || "en",
    );
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
      restartAttemptRef.current = 0;
      resultCommitCursorRef.current = 0;
      latestResultsLengthRef.current = 0;
      utteranceBufferRef.current = "";

      const t = recognitionTimersRef.current;
      const opts = stateRef.current.options;
      const firstWordMs = opts.firstWordGraceMs ?? DEFAULT_FIRST_WORD_GRACE_MS;
      if (t.firstWord) clearTimeout(t.firstWord);
      t.firstWord = setTimeout(() => {
        t.firstWord = null;
      }, firstWordMs);
    };

    recognition.onresult = (event) => {
      latestResultsLengthRef.current = event.results.length;

      if (stateRef.current.isProcessing) return;

      const t = recognitionTimersRef.current;
      const opts = stateRef.current.options;
      const silenceMs = opts.silenceThresholdMs ?? DEFAULT_SILENCE_THRESHOLD_MS;
      const maxMs = opts.maxUtteranceMs ?? DEFAULT_MAX_UTTERANCE_MS;

      const startIdx = resultCommitCursorRef.current;
      let buffer = "";
      for (let i = startIdx; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = result?.[0]?.transcript ?? "";
        buffer += transcript;
      }
      const trimmedBuffer = buffer.trim();
      if (!trimmedBuffer) return;

      utteranceBufferRef.current = trimmedBuffer;

      if (t.firstWord) {
        clearTimeout(t.firstWord);
        t.firstWord = null;
      }

      if (t.silence) clearTimeout(t.silence);
      t.silence = setTimeout(() => {
        t.silence = null;
        resultCommitCursorRef.current = event.results.length;
        commitUtterance();
      }, silenceMs);

      if (!t.max) {
        t.max = setTimeout(() => {
          t.max = null;
          resultCommitCursorRef.current = event.results.length;
          commitUtterance();
        }, maxMs);
      }
    };

    recognition.onerror = (event) => {
      if (
        event.error === "not-allowed" ||
        event.error === "service-not-allowed"
      ) {
        setError(`Microphone permission required (${event.error}).`);
        setIsListening(false);
        stateRef.current.shouldKeepListening = false;
        return;
      }
      console.warn(`Speech recognition transient error: ${event.error}`);
    };

    recognition.onend = () => {
      if (
        stateRef.current.shouldKeepListening &&
        !stateRef.current.isProcessing &&
        utteranceBufferRef.current.trim()
      ) {
        commitUtterance();
      } else {
        const t = recognitionTimersRef.current;
        if (t.silence) {
          clearTimeout(t.silence);
          t.silence = null;
        }
        if (t.max) {
          clearTimeout(t.max);
          t.max = null;
        }
        if (t.firstWord) {
          clearTimeout(t.firstWord);
          t.firstWord = null;
        }
      }

      if (!stateRef.current.shouldKeepListening) {
        setIsListening(false);
        return;
      }
      scheduleRestart();
    };

    return recognition;
  };

  const startRecognition = useCallback(() => {
    if (!stateRef.current.recognition) {
      const recognition = buildRecognitionInstance();
      if (!recognition) return;
      stateRef.current.recognition = recognition;
    } else {
      stateRef.current.recognition.lang = getLocaleLanguage(
        stateRef.current.options.language || "en",
      );
    }
    safeStartEngine();
  }, []);

  useEffect(() => {
    startRecognitionRef.current = startRecognition;
  }, [startRecognition]);

  const restartRecognitionIfWanted = () => {
    if (!stateRef.current.shouldKeepListening) return;
    resultCommitCursorRef.current = latestResultsLengthRef.current;
    utteranceBufferRef.current = "";
    const t = recognitionTimersRef.current;
    if (t.silence) {
      clearTimeout(t.silence);
      t.silence = null;
    }
    if (t.max) {
      clearTimeout(t.max);
      t.max = null;
    }
    startRecognitionRef.current?.();
  };

  type VoiceMessage = {
    response?: string;
    toolUses?: Array<
      | { type: "fillField"; fieldUri: string; value?: any; id: string }
      | { type: "moveToNextSection"; id: string }
      | { type: "submit"; id: string }
      | { type: "scrollToFileField"; fieldUri: string; id: string }
    >;
    type?: string;
    message?: string;
    audioUrl?: string;
  };

  const handleMessage = useCallback(async (message: unknown) => {
    const data = (message ?? {}) as VoiceMessage;
    if (!expectingReplyRef.current) {
      console.warn(
        "Discarding voice reply that arrived without a pending request " +
          "(likely after reply timeout or stopListening).",
      );
      return;
    }
    clearReplyTimeout();
    const opts = stateRef.current.options;
    const { submissionUri } = opts;
    if (!submissionUri) {
      return;
    }
    const language = opts.language || "en";

    try {
      if (data.toolUses && data.toolUses.length > 0) {
        stateRef.current.conversationHistory.push({
          role: "assistant",
          content: data.toolUses.map((toolUse) => ({
            type: "tool_use",
            id: toolUse.id,
            name: toolUse.type,
            input:
              toolUse.type === "fillField"
                ? { fieldUri: toolUse.fieldUri, value: toolUse.value }
                : {},
          })),
        });

        const toolResults = processActions(data.toolUses);

        if (data.toolUses.some((toolUse) => toolUse.type === "submit")) {
          stateRef.current.shouldKeepListening = false;
          setIsListening(false);
          if (stateRef.current.recognition) {
            stateRef.current.recognition.stop();
          }
        }

        const resultById = new Map(toolResults.map((r) => [r.id, r]));
        stateRef.current.conversationHistory.push({
          role: "user",
          content: data.toolUses.map((toolUse) => {
            const r = resultById.get(toolUse.id);
            const content =
              r?.success === true ? "success" : (r?.reason ?? "unknown error");
            return {
              type: "tool_result" as const,
              tool_use_id: toolUse.id,
              content,
            };
          }),
        });

        const hadMoveToNextSection = data.toolUses.some(
          (t) => t.type === "moveToNextSection",
        );
        let effectiveCurrentSectionUri = opts.selectedSection?.uri;
        if (
          hadMoveToNextSection &&
          opts.visibleSections &&
          opts.selectedSection
        ) {
          const selectedIndex = opts.visibleSections.findIndex(
            (s) => s.uri === opts.selectedSection?.uri,
          );
          const nextSection = opts.visibleSections[selectedIndex + 1];
          effectiveCurrentSectionUri =
            nextSection?.uri ?? opts.selectedSection?.uri;
        }

        try {
          await stateRef.current.websocket?.send({
            submissionUri,
            preview: opts.preview,
            currentSectionUri: effectiveCurrentSectionUri || "",
            conversationHistory: stateRef.current.conversationHistory,
            language,
          });
          setReplyTimeout();
        } catch (err) {
          console.error("Error sending tool results", err);
          stateRef.current.isProcessing = false;
          setError("Failed to send tool results.");
          restartRecognitionIfWanted();
        }

        return;
      }

      if (data.response) {
        const aiResponse = data.response;
        stateRef.current.conversationHistory.push({
          role: "assistant",
          content: aiResponse,
        });

        const finishTurn = () => {
          stateRef.current.isProcessing = false;
          restartRecognitionIfWanted();
        };

        if (!data.audioUrl) {
          console.warn("No audio url received; finishing turn silently.");
          finishTurn();
          return;
        }

        playResponse(data.audioUrl, finishTurn);
      } else if (data.type === "error" && data.message) {
        stateRef.current.isProcessing = false;
        setError(data.message || "An error occurred.");
        if (!data.audioUrl) {
          console.warn("No audio url received for error message");
          restartRecognitionIfWanted();
        } else {
          playResponse(data.audioUrl, () => {
            restartRecognitionIfWanted();
          });
        }
      } else {
        console.warn(
          "Voice websocket message matched no branch; finishing turn.",
          data,
        );
        stateRef.current.isProcessing = false;
        restartRecognitionIfWanted();
      }
    } catch (err) {
      console.error("Error handling websocket message", err);
      stateRef.current.isProcessing = false;
      setError("Failed to process server response.");
      restartRecognitionIfWanted();
    }
  }, []);

  useEffect(() => {
    websocket.setOnMessage(handleMessage);
    return () => {
      websocket.setOnMessage(null);
    };
  }, []);

  const startListening = useCallback(async () => {
    if (stateRef.current.isInitialized) {
      return;
    }

    stateRef.current.isInitialized = true;
    stateRef.current.shouldKeepListening = true;
    stateRef.current.conversationHistory = [];
    stateRef.current.lastProcessedTranscript = "";
    stateRef.current.isProcessing = false;

    if (!options?.submissionUri) {
      return;
    }

    try {
      await stateRef.current.websocket?.connect();

      const initialGreeting = getInitialGreeting(selectedLanguage);
      stateRef.current.conversationHistory.push({
        role: "user",
        content: initialGreeting,
      });

      stateRef.current.isProcessing = true;
      await stateRef.current.websocket?.send({
        submissionUri: options.submissionUri!,
        preview: options.preview,
        currentSectionUri:
          stateRef.current.options?.selectedSection?.uri ||
          stateRef.current.options?.visibleSections?.[0]?.uri ||
          "",
        conversationHistory: stateRef.current.conversationHistory,
        language: selectedLanguage,
      });
      setReplyTimeout();
    } catch (err) {
      console.error(err);
      setError("Failed to start voice conversation.");
      stateRef.current.isInitialized = false;
      stateRef.current.isProcessing = false;
      clearReplyTimeout();
    }
  }, [
    options?.submissionUri,
    selectedLanguage,
    JSON.stringify(options?.preview?.responses),
  ]);

  const stopListening = useCallback(() => {
    stateRef.current.isInitialized = false;
    stateRef.current.shouldKeepListening = false;
    stateRef.current.isProcessing = false;
    clearRecognitionTimers();
    clearReplyTimeout();
    stopUploadPolling();
    pendingMoveAfterUploadRef.current = false;
    clearFileSelectListener();
    if (stateRef.current.recognition) {
      stateRef.current.recognition.abort();
      stateRef.current.recognition = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      setIsSpeaking(false);
    }
    utteranceBufferRef.current = "";
    resultCommitCursorRef.current = 0;
    restartAttemptRef.current = 0;
    stateRef.current.websocket?.disconnect();
    setIsListening(false);
  }, []);

  return {
    isListening,
    isSpeaking,
    error,
    startListening,
    stopListening,
    playResponse,
    isSupported: getSpeechRecognition() !== null && !!options?.voiceWebSocket,
  };
};
